import {useDispatch, useSelector} from "react-redux";
import useToast from "../../../util/useToast";
import {useCallback, useEffect, useMemo, useState} from "react";
import {AMOUNT_REGEX, MODE_EDIT} from "../../../codes";
import {useTranslation} from "react-i18next";
import {createBudgetCategory, updateBudgetCategory} from "../../../api/ApiBudgetCategories";
import {selectAllBudgets} from "../../../slices/budgetsSlice";
import {
  editorConfigSelector, listConfigSelector,
  rxHideBudgetCategoryEditor,
  rxShowHideDeleteConfirmationModal,
  rxUpdateBudgetCategories, selectAllBudgetCategories
} from "../../../slices/budgetCategoriesSlice";
import {getOrDefault} from "../../../util/util";
import {selectAllCategories} from "../../../slices/categoriesSlice";
import useNetwork from "../../../util/useNetwork";
import useValidator from "../../../util/useValidator";

export const TYPE_EXPENSE = 1;
export const TYPE_INCOME = 2;

const useBudgetCategoryEditor = () => {
  const dispatch = useDispatch();

  const {t} = useTranslation("budgets");
  const {showToastSuccess} = useToast();
  const {handleNetworkError} = useNetwork();
  const validate = useValidator();

  const budget = useSelector(selectAllBudgets)[0];

  const {currentYear, currentMonth} = useSelector(listConfigSelector);
  const {show, mode, category, deleteConfirmationModal} = useSelector(editorConfigSelector);

  const valueDate = currentYear + "-" + (currentMonth + 1 < 10 ? "0" : "") + (currentMonth + 1) + "-01";

  const transactionCategories = useSelector(selectAllCategories);
  const budgetCategories = useSelector(selectAllBudgetCategories);

  const getCategories = useCallback(period => {
    return transactionCategories
      .map(category => {
        const budgetCats = budgetCategories
          .filter(bc => bc.valueDate <= period && bc.category === category.id)
          .toSorted((a, b) => b.valueDate.localeCompare(a.valueDate));

        if (budgetCats.length === 0 || budgetCats[0].amount === 0) {
          return null;
        } else {
          return {category, budgetCat: budgetCats[0]};
        }
      })
      .filter(c => c !== null);
  }, [budgetCategories, transactionCategories]);

  const currentPeriodCategories = useMemo(() => getCategories(valueDate), [getCategories, valueDate]);

  const [editedCategory, setEditedCategory] = useState({errors: {}, amount: ""});
  const [availableCategories, setAvailableCategories] = useState([]);

  const hideBudgetCategoryEditor = () => {
    dispatch(rxHideBudgetCategoryEditor());
  };

  useEffect(() => {
    if (!category || !show) {
      return;
    }

    const avCategories = transactionCategories.filter(c => (c.active && !currentPeriodCategories.find(cc => cc.category.id === c.id)) || c.id === category.category);

    const values = {
      type: category.amount === undefined || category.amount < 0 ? TYPE_EXPENSE : TYPE_INCOME,
      errors: {},
      amount: category.amount === undefined ? "" : "" + Math.abs(category.amount),
      category: getOrDefault(category.category, avCategories[0].id)
    };

    setEditedCategory(values);

    setAvailableCategories(avCategories);
  }, [show, category, currentPeriodCategories, transactionCategories]);

  const getErrors = values => {
    const errors = {};

    if (values.amount === undefined) {
      errors.amount = "budget_category_editor_error_amount_must_be_filled";
    }

    return errors;
  };

  const submit = e => {
    e.preventDefault();

    let amount;

    if (editedCategory.amount.length) {
      amount = +editedCategory.amount.replace(",", ".");
    }

    if (editedCategory.type === TYPE_EXPENSE) {
      amount = 0 - amount;
    }

    const values = {
      valueDate,
      amount,
      category: editedCategory.category
    };

    return validate(getErrors(values))
      .then(() => {
        if (mode === MODE_EDIT) {
          return updateBudgetCategory(budget.id, category.id, values);
        } else {
          return createBudgetCategory(budget.id, values);
        }
      })
      .then(result => dispatch(rxUpdateBudgetCategories(result)))
      .then(() => {
        showToastSuccess(t(mode === MODE_EDIT ? "category_edited" : "category_added"));

        hideBudgetCategoryEditor();
      })
      .catch(error => {
        handleNetworkError(error, {
          400: err => setEditedCategory(prev => ({...prev, errors: err.response.data}))
        });
      });
  };

  const updateEditedCategory = field => event => {
    setEditedCategory(prev => {
      const values = {};

      switch (field) {
        case "type":
        case "category":
          values[field] = event;
          break;
        case "amount": {
          const amount = event.target.value;

          if (AMOUNT_REGEX.test(amount)) {
            values.amount = amount;
          }

          break;
        }
        default:
          values[field] = event.target.value;
          break;
      }

      return ({...prev, ...values});
    });
  };

  const showDeleteConfirmationModal = useCallback((() => {
    dispatch(rxShowHideDeleteConfirmationModal(true));
  }), [dispatch]);

  const hideDeleteConfirmationModal = useCallback((() => {
    dispatch(rxShowHideDeleteConfirmationModal(false));
  }), [dispatch]);

  const onDeleteBudgetCategory = () => {
    const values = {
      valueDate,
      amount: 0,
      category: category.category
    };

    return updateBudgetCategory(budget.id, category.id, values)
      .then(result => {
        dispatch(rxUpdateBudgetCategories(result));
        dispatch(rxHideBudgetCategoryEditor());
      });
  };

  return {
    show,
    t,
    mode,
    submit,
    hideBudgetCategoryEditor,
    category,
    editedCategory,
    availableCategories,
    updateEditedCategory,
    deleteConfirmationModal,
    showDeleteConfirmationModal,
    hideDeleteConfirmationModal,
    onDeleteBudgetCategory
  };
};

export default useBudgetCategoryEditor;
